import React from 'react'
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles'
import { Paper, Typography, useMediaQuery } from '@material-ui/core'
import { GridColDef, GridCellEditCommitParams } from '@material-ui/data-grid'
import DataTable from '../DataTable/DataTable'

/**
 * Columna de un listado responsive.
 * - widthPct: porcentaje del ancho de la tabla. Las columnas visibles se normalizan para
 *   sumar siempre 100%, así la tabla ocupa todo el ancho en cualquier resolución.
 * - hideOnTablet: se oculta entre 600 y 959px (los % de las demás se redistribuyen).
 * - cardTitle: campo que se usa como título de la card en mobile (por defecto, la primera columna).
 * - hideInCard: no se muestra en la card.
 * - renderCard: contenido propio del campo en la card (por ejemplo, un badge de estado).
 * Las columnas de tipo 'actions' no se muestran en la card; usar `cardActions`.
 */
export interface ResponsiveColumn extends Omit<GridColDef, 'flex' | 'width'> {
  widthPct: number
  hideOnTablet?: boolean
  cardTitle?: boolean
  hideInCard?: boolean
  renderCard?: (row: any) => React.ReactNode
}

interface Props {
  keyId: string
  rows: any[]
  columns: ResponsiveColumn[]
  gridEdit?: (params: GridCellEditCommitParams) => void
  // contenido del pie de cada card en mobile (botones de acción)
  cardActions?: (row: any) => React.ReactNode
  emptyMessage?: string
}

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
    cardTitle: {
      fontWeight: 600,
      wordBreak: 'break-word',
      marginBottom: theme.spacing(1),
    },
    cardFields: {
      margin: 0,
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(0.5),
      '& dt': {
        color: theme.palette.text.secondary,
      },
      '& dd': {
        margin: 0,
        textAlign: 'right',
        wordBreak: 'break-word',
      },
    },
    cardActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  }),
)

export default function ResponsiveList({ keyId, rows, columns, gridEdit, cardActions, emptyMessage = 'No hay registros para mostrar.' }: Props) {
  const classes = useStyles()
  const theme = useTheme()
  // xs (<600): cards · sm (600-959): tabla sin columnas secundarias · md+: tabla completa
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'sm'))

  if (isMobile) {
    const titleColumn = columns.find(c => c.cardTitle) ?? columns[0]
    const fieldColumns = columns.filter(c => c !== titleColumn && !c.hideInCard && c.type !== 'actions')

    if (rows.length === 0) {
      return <Typography color="textSecondary">{emptyMessage}</Typography>
    }

    const format = (column: ResponsiveColumn, row: any) => {
      const value = column.valueGetter
        ? column.valueGetter({ id: row[keyId], field: column.field, row, getValue: (_id: any, field: string) => row[field] } as any)
        : row[column.field]
      const formatted = column.valueFormatter
        ? column.valueFormatter({ value, field: column.field, id: row[keyId], row } as any)
        : value
      return formatted === undefined || formatted === null || formatted === '' ? '—' : String(formatted)
    }

    return (
      <div>
        {rows.map(row => (
          <Paper key={row[keyId]} variant="outlined" className={classes.card}>
            <Typography className={classes.cardTitle}>{format(titleColumn, row)}</Typography>
            <dl className={classes.cardFields}>
              {fieldColumns.map(column => (
                <React.Fragment key={column.field}>
                  <dt>{column.headerName}</dt>
                  <dd>{column.renderCard ? column.renderCard(row) : format(column, row)}</dd>
                </React.Fragment>
              ))}
            </dl>
            {cardActions && <div className={classes.cardActions}>{cardActions(row)}</div>}
          </Paper>
        ))}
      </div>
    )
  }

  const visibleColumns = columns.filter(c => !(isTablet && c.hideOnTablet))
  const totalPct = visibleColumns.reduce((sum, c) => sum + c.widthPct, 0)
  const gridColumns: GridColDef[] = visibleColumns.map(({ widthPct, hideOnTablet, cardTitle, hideInCard, renderCard, ...column }) => ({
    ...column,
    // flex proporcional a widthPct/total: las columnas siempre suman el 100% del ancho
    flex: (widthPct / totalPct) * 100,
  }))

  return <DataTable rows={rows} columns={gridColumns} keyId={keyId} gridEdit={gridEdit} />
}
